import { useQuery, useQueries } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { CheckSquare, MessageSquareText, type LucideIcon } from 'lucide-react';

export const systemKeys = {
  all: ['system'] as const,
  health: () => [...systemKeys.all, 'health'] as const,
  tasks: () => [...systemKeys.all, 'tasks'] as const,
};

export function useSystemHealth() {
  return useQuery({
    queryKey: systemKeys.health(),
    queryFn: () => apiClient.system.getHealth(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export interface PendingTask {
  id: string;
  title: string;
  program: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  btnColor: string;
  btnLabel: string;
  to: string;
  count: number;
}

export function usePendingTasks() {
  const results = useQueries({
    queries: [
      {
        queryKey: ['registrations', 'pending', 'count'],
        queryFn: () => apiClient.registrations.list({ status: 'PENDING', page: 1, limit: 1 }),
      },
      {
        queryKey: ['reflections', 'pending', 'count'],
        queryFn: () => apiClient.reflections.list({ status: 'PENDING', page: 1, limit: 1 }),
      },
    ],
  });

  const [pendingRegs, pendingReflections] = results;

  const isLoading = results.some(r => r.isLoading);
  const data: PendingTask[] = [];

  if (!isLoading) {
    if (pendingRegs.data?.meta.total && pendingRegs.data.meta.total > 0) {
      data.push({
        id: 'task-registrations',
        title: `Approve ${pendingRegs.data.meta.total} New Registrations`,
        program: 'Awaiting administrative review',
        icon: CheckSquare,
        color: 'text-primary',
        bgColor: 'bg-primary-fixed',
        btnColor: 'text-primary border-primary-fixed',
        btnLabel: 'Approve Now',
        to: '/registrations',
        count: pendingRegs.data.meta.total,
      });
    }

    if (pendingReflections.data?.meta.total && pendingReflections.data.meta.total > 0) {
      data.push({
        id: 'task-reflections',
        title: `Review ${pendingReflections.data.meta.total} Reflections`,
        program: 'Learners waiting for feedback',
        icon: MessageSquareText,
        color: 'text-secondary',
        bgColor: 'bg-secondary-container',
        btnColor: 'text-secondary border-secondary-container',
        btnLabel: 'Launch Review',
        to: '/materials/review',
        count: pendingReflections.data.meta.total,
      });
    }
  }

  return { data, isLoading };
}
